/**
 * A porta da apuração (ORD-F-153 e complemento de 20/10 20:40, decisão do responsável; DECISOES §84). A página principal
 * continua pública (página inicial, pré-apuração e, a partir das 17h00 de 25/10, a capa com a live do YouTube); só a
 * apuração ao vivo fica atrás do login (Firebase Authentication pela API REST, sem SDK: data/authClient.ts).
 *
 * A decisão sai ANTES de o app montar (main.tsx), lendo só o aparelho: com sessão guardada, o site abre direto, sem
 * consultar o login (os números vêm do feed, que é público). Sem sessão, nenhum pedido ao feed: nem a pré-busca do boot
 * (src/boot/prefetch.ts, mesma regra) nem o FeedClient, que não é criado. A sessão só cai com "Sair" ou com uma recusa
 * definitiva do refresh token; falha de rede na renovação não desloga (a renovação roda em segundo plano).
 */
import type { FeatureFlags } from '../config';
import {
  type AuthClientOptions,
  type AuthFailure,
  refreshIdToken,
  sendPasswordReset,
  signInWithPassword,
} from '../data/authClient';
import { fetchOriginClockOffset } from '../data/originClock';
import { type AuthSession, clearAuthSession, readAuthSession, writeAuthSession } from '../state/storage';

/**
 * `open`: sem a porta (o site de hoje). `session`: a porta ligada e sessão guardada (o site completo). `public`: sem
 * sessão, ou o pacote só da capa (`capa_only`): a página principal pública, sem feed.
 */
export type SiteAccess = 'open' | 'session' | 'public';

/** Pura: o acesso desta carga pelas chaves e por haver sessão guardada. */
export function siteAccess(features: Pick<FeatureFlags, 'login_gate' | 'capa_only'>, hasSession: boolean): SiteAccess {
  if (features.capa_only) return 'public';
  if (!features.login_gate) return 'open';
  return hasSession ? 'session' : 'public';
}

/** A chave pública do app web (VITE_AUTH_API_KEY); vazia até o responsável registrar o app no console. */
export const AUTH_API_KEY: string = import.meta.env.VITE_AUTH_API_KEY ?? '';

function client(): AuthClientOptions {
  return { apiKey: AUTH_API_KEY };
}

/** O e-mail da sessão guardada ('' sem sessão). */
export function sessionEmail(): string {
  return readAuthSession()?.email ?? '';
}

/** Recarrega a página: a decisão da porta é sempre a do boot (com sessão, o app; sem, a página pública). */
function reload(): void {
  location.reload();
}

/** Entrar: grava a sessão e recarrega. Devolve o motivo da recusa (o formulário mostra o texto neutro). */
export async function signIn(email: string, password: string): Promise<AuthFailure | null> {
  const r = await signInWithPassword(client(), email.trim(), password);
  if (!r.ok) return r.reason;
  const t = r.tokens;
  writeAuthSession({ email: t.email, refreshToken: t.refreshToken, idToken: t.idToken, expiresAt: t.expiresAt });
  reload();
  return null;
}

/** "Esqueci a senha": null = pedido aceito (texto neutro, exista ou não a conta). */
export async function requestPasswordReset(email: string): Promise<'throttled' | 'config' | 'network' | null> {
  const r = await sendPasswordReset(client(), email.trim());
  return r.ok ? null : r.reason;
}

/** "Sair": apaga a sessão e recarrega (o feed para junto com a página). */
export function signOut(): void {
  clearAuthSession();
  reload();
}

// ── renovação em segundo plano ────────────────────────────────────────────────────────────────────────────────────
/** Renova quando faltam menos de 5 min para o id token vencer. */
export const REFRESH_AHEAD_MS = 5 * 60_000;
/** A 1ª renovação espera a 1ª pintura passar (não disputa a rede com o feed nem com o LCP). */
export const REFRESH_FIRST_DELAY_MS = 5_000;
/** Recuo depois de uma falha passageira: 1 min, dobrando até 15 min. A sessão continua valendo. */
export const REFRESH_RETRY_MIN_MS = 60_000;
export const REFRESH_RETRY_MAX_MS = 15 * 60_000;

export interface KeeperDeps {
  read(): AuthSession | null;
  write(s: AuthSession): void;
  clear(): void;
  refresh(refreshToken: string): ReturnType<typeof refreshIdToken>;
  now(): number;
  setTimer(fn: () => void, ms: number): unknown;
  clearTimer(id: unknown): void;
  /** Recusa definitiva (conta desativada, token revogado): a sessão já foi apagada. */
  onRevoked(): void;
}

/**
 * Mantém a sessão viva em segundo plano. Uma falha de rede (ou o serviço do Google fora do ar, ou a chave recusada)
 * nunca desloga: a sessão fica e tenta de novo mais tarde. Só `revoked` apaga e chama `onRevoked`. Devolve o desligamento.
 */
export function startSessionKeeper(deps: KeeperDeps): () => void {
  let timer: unknown = null;
  let stopped = false;
  let retry = REFRESH_RETRY_MIN_MS;
  const schedule = (ms: number) => {
    if (stopped) return;
    if (timer !== null) deps.clearTimer(timer);
    timer = deps.setTimer(run, Math.max(0, ms));
  };
  const untilDue = (s: AuthSession) => s.expiresAt - REFRESH_AHEAD_MS - deps.now();
  async function run(): Promise<void> {
    timer = null;
    if (stopped) return;
    const s = deps.read();
    if (!s) return;
    const wait = untilDue(s);
    if (wait > 0) {
      schedule(wait);
      return;
    }
    const r = await deps.refresh(s.refreshToken);
    if (stopped) return;
    if (r.ok) {
      retry = REFRESH_RETRY_MIN_MS;
      const next: AuthSession = { ...s, ...r.tokens };
      deps.write(next);
      schedule(untilDue(next));
      return;
    }
    if (r.reason === 'revoked') {
      deps.clear();
      deps.onRevoked();
      return;
    }
    schedule(retry);
    retry = Math.min(REFRESH_RETRY_MAX_MS, retry * 2);
  }
  schedule(REFRESH_FIRST_DELAY_MS);
  return () => {
    stopped = true;
    if (timer !== null) deps.clearTimer(timer);
    timer = null;
  };
}

/** O guardião de verdade (main.tsx, só com acesso `session`). */
export function keepSessionAlive(): () => void {
  return startSessionKeeper({
    read: readAuthSession,
    write: (s) => {
      writeAuthSession(s);
    },
    clear: clearAuthSession,
    refresh: (token) => refreshIdToken(client(), token),
    now: Date.now,
    setTimer: (fn, ms) => setTimeout(fn, ms),
    clearTimer: (id) => clearTimeout(id as ReturnType<typeof setTimeout>),
    onRevoked: reload,
  });
}

// ── relógio da página pública ─────────────────────────────────────────────────────────────────────────────────────
let originOffsetMs = 0;

/** O relógio da página pública: o do aparelho, corrigido pelo `Date` da própria origem (como no M4, sem o feed). */
export function publicClock(): number {
  return Date.now() + originOffsetMs;
}

/** Lê o `Date` de um `HEAD /` (uma vez por carga) e corrige o relógio público; falha = o relógio do aparelho. */
export function learnOriginClock(): Promise<void> {
  return fetchOriginClockOffset('/').then((off) => {
    if (off !== null) originOffsetMs = off;
  });
}
